import type { PermissionModule } from '../../lib/permissionsApi';

/**
 * UI metadata describing how to render the {@link UserPermissionMatrix} as an
 * editable matrix (CLE-216, parent section 3.5). Each module has a label, the
 * four primary CRUD flags (mapped to the module's actual flag keys, since they
 * vary — e.g. financials has no canCreate), an optional scope dropdown, and a
 * list of nested "sub-permission" toggles.
 *
 * Keep this in sync with backend/src/lib/permissions/matrix.ts.
 */

export interface ScopeConfig {
  /** The matrix flag holding the scope value, e.g. 'scope'. */
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface SubPermissionConfig {
  key: string;
  label: string;
  /** Parent flag key — when the parent is off, this child is forced off. */
  parent?: string;
}

export interface ModuleConfig {
  module: PermissionModule;
  label: string;
  /** The primary CRUD action flags present on this module (in order). */
  crud: { key: string; label: string }[];
  scope?: ScopeConfig;
  /** Nested sub-permission toggles (rendered below the CRUD row). */
  sub: SubPermissionConfig[];
}

const PROJECT_SCOPE: ScopeConfig = {
  key: 'scope',
  label: 'Domeniu proiecte',
  options: [
    { value: 'all', label: 'Toate proiectele' },
    { value: 'assigned', label: 'Doar cele atribuite' },
    { value: 'specific', label: 'Proiecte specifice' },
  ],
};

const TASK_SCOPE: ScopeConfig = {
  key: 'scope',
  label: 'Domeniu sarcini',
  options: [
    { value: 'all', label: 'Toate sarcinile' },
    { value: 'assigned', label: 'Atribuite mie' },
    { value: 'project_scoped', label: 'În proiectele mele' },
  ],
};

const MEETING_SCOPE: ScopeConfig = {
  key: 'scope',
  label: 'Domeniu întâlniri',
  options: [
    { value: 'all', label: 'Toate întâlnirile' },
    { value: 'involved', label: 'Doar cele la care particip' },
  ],
};

const SUMMARY_SCOPE: ScopeConfig = {
  key: 'scope',
  label: 'Domeniu',
  options: [
    { value: 'self', label: 'Doar pe mine' },
    { value: 'all', label: 'Toți' },
  ],
};

const CRUD = (extra?: { create?: boolean; del?: boolean }) => {
  const list = [
    { key: 'canView', label: 'Vizualizare' },
    { key: 'canCreate', label: 'Creare' },
    { key: 'canEdit', label: 'Editare' },
    { key: 'canDelete', label: 'Ștergere' },
  ];
  return list.filter(
    (c) =>
      !(c.key === 'canCreate' && extra?.create === false) &&
      !(c.key === 'canDelete' && extra?.del === false),
  );
};

export const PERMISSION_MATRIX_CONFIG: ModuleConfig[] = [
  {
    module: 'dashboard',
    label: 'Dashboard',
    crud: [{ key: 'canView', label: 'Vizualizare' }],
    sub: [
      { key: 'canViewFinancialCards', label: 'Carduri financiare', parent: 'canView' },
      { key: 'canViewTeamAllocation', label: 'Alocare echipă', parent: 'canView' },
      { key: 'canViewActivityFeed', label: 'Flux de activitate', parent: 'canView' },
    ],
  },
  {
    module: 'projects',
    label: 'Proiecte',
    crud: CRUD(),
    scope: PROJECT_SCOPE,
    sub: [
      { key: 'canViewOverview', label: 'Vizualizare prezentare generală', parent: 'canView' },
      { key: 'canViewFinancials', label: 'Vizualizare financiar', parent: 'canView' },
      { key: 'canEditFinancials', label: 'Editare financiar', parent: 'canEdit' },
      { key: 'canViewTeam', label: 'Vizualizare echipă', parent: 'canView' },
      { key: 'canEditTeam', label: 'Editare echipă', parent: 'canEdit' },
      { key: 'canViewResources', label: 'Vizualizare resurse', parent: 'canView' },
      { key: 'canEditResources', label: 'Editare resurse', parent: 'canEdit' },
      { key: 'canViewMeetingNotes', label: 'Vizualizare note întâlniri', parent: 'canView' },
      { key: 'canEditMeetingNotes', label: 'Editare note întâlniri', parent: 'canEdit' },
      { key: 'canViewVault', label: 'Vizualizare vault (credențiale)', parent: 'canView' },
      { key: 'canRevealVault', label: 'Dezvăluire parole din vault', parent: 'canViewVault' },
      { key: 'canEditVault', label: 'Editare vault (credențiale)', parent: 'canEdit' },
    ],
  },
  {
    module: 'tasks',
    label: 'Sarcini',
    crud: CRUD(),
    scope: TASK_SCOPE,
    sub: [
      { key: 'canBulkEdit', label: 'Editare în masă', parent: 'canEdit' },
      { key: 'canViewReports', label: 'Vizualizare rapoarte', parent: 'canView' },
    ],
  },
  {
    module: 'leads',
    label: 'Pipeline',
    crud: CRUD(),
    sub: [
      { key: 'canConvert', label: 'Conversie în proiect', parent: 'canEdit' },
      { key: 'canViewActions', label: 'Vizualizare acțiuni', parent: 'canView' },
      { key: 'canEditActions', label: 'Editare acțiuni', parent: 'canEdit' },
      { key: 'canConfigurePipeline', label: 'Configurare pipeline', parent: 'canEdit' },
    ],
  },
  {
    module: 'clients',
    label: 'Clienți',
    crud: CRUD(),
    sub: [
      { key: 'canViewContacts', label: 'Vizualizare contacte', parent: 'canView' },
      { key: 'canEditContacts', label: 'Editare contacte', parent: 'canEdit' },
      { key: 'canViewResources', label: 'Vizualizare resurse', parent: 'canView' },
      { key: 'canEditResources', label: 'Editare resurse', parent: 'canEdit' },
      { key: 'canViewMeetingNotes', label: 'Vizualizare note întâlniri', parent: 'canView' },
      { key: 'canEditMeetingNotes', label: 'Editare note întâlniri', parent: 'canEdit' },
    ],
  },
  {
    module: 'meetings',
    label: 'Întâlniri',
    crud: CRUD(),
    scope: MEETING_SCOPE,
    sub: [
      { key: 'canViewNotes', label: 'Vizualizare notițe și transcrieri', parent: 'canView' },
      { key: 'canEditNotes', label: 'Adăugare/editare notițe și transcrieri', parent: 'canEdit' },
      { key: 'canManageContacts', label: 'Gestionare persoane de contact', parent: 'canEdit' },
    ],
  },
  {
    module: 'videoMeetings',
    label: 'Firmio Meet',
    crud: [
      { key: 'canView', label: 'Vizualizare' },
      { key: 'canCreate', label: 'Creare' },
    ],
    sub: [{ key: 'canManage', label: 'Închide/anulează întâlnirile altora', parent: 'canView' }],
  },
  {
    module: 'socialPosts',
    label: 'Calendar de postări',
    crud: [
      { key: 'canView', label: 'Vizualizare' },
      { key: 'canCreate', label: 'Creare' },
      { key: 'canEdit', label: 'Editare' },
      { key: 'canDelete', label: 'Ștergere' },
    ],
    sub: [
      {
        key: 'canManageShareLinks',
        label: 'Generează/revocă linkul de calendar pentru client',
        parent: 'canView',
      },
    ],
  },
  {
    module: 'leave',
    label: 'Concedii',
    // No "Vizualizare": every angajat sees and files their own leave without a
    // permission. These three are only about other people's requests, so
    // "Vede toate cererile" is the parent the other two hang off.
    crud: [{ key: 'canViewAll', label: 'Vede cererile tuturor' }],
    sub: [
      { key: 'canApprove', label: 'Aprobă/respinge cereri', parent: 'canViewAll' },
      {
        key: 'canManage',
        label: 'Editează cererile altora și zilele cuvenite',
        parent: 'canViewAll',
      },
    ],
  },
  {
    module: 'suppliers',
    label: 'Furnizori',
    crud: CRUD(),
    sub: [],
  },
  {
    module: 'actors',
    label: 'Actori',
    crud: CRUD(),
    sub: [],
  },
  {
    module: 'whatsapp',
    label: 'WhatsApp',
    crud: [{ key: 'canView', label: 'Vede conversațiile' }],
    sub: [{ key: 'canConfigure', label: 'Conectare și grupuri', parent: 'canView' }],
  },
  {
    module: 'subscriptions',
    label: 'Abonamente',
    crud: CRUD(),
    sub: [],
  },
  {
    module: 'financials',
    label: 'Financiar',
    // Financials has no top-level create/delete; the granular flags are subs.
    crud: [{ key: 'canView', label: 'Vizualizare' }],
    sub: [
      { key: 'canViewPnL', label: 'Vizualizare P&L', parent: 'canView' },
      { key: 'canViewInvoices', label: 'Vizualizare facturi', parent: 'canView' },
      { key: 'canCreateInvoices', label: 'Creare facturi', parent: 'canView' },
      { key: 'canEditInvoices', label: 'Editare facturi', parent: 'canView' },
      { key: 'canDeleteInvoices', label: 'Ștergere facturi', parent: 'canView' },
      { key: 'canViewSpv', label: 'Vizualizare SPV (e-Factura)', parent: 'canView' },
      { key: 'canSyncSpv', label: 'Sincronizare SPV (e-Factura)', parent: 'canView' },
      { key: 'canIssueInvoices', label: 'Emitere facturi (ciorne)', parent: 'canView' },
      {
        key: 'canApproveInvoices',
        label: 'Aprobare și trimitere facturi în SPV',
        parent: 'canView',
      },
      { key: 'canViewBankStatements', label: 'Vizualizare extrase bancare', parent: 'canView' },
      { key: 'canUploadBankStatements', label: 'Încărcare extrase bancare', parent: 'canView' },
      { key: 'canReconcile', label: 'Reconciliere', parent: 'canView' },
      { key: 'canExportReports', label: 'Export rapoarte', parent: 'canView' },
      { key: 'canSendReminders', label: 'Trimitere remindere facturi', parent: 'canView' },
      {
        key: 'canManageScheduledPayments',
        label: 'Gestionare plăți/încasări planificate',
        parent: 'canView',
      },
    ],
  },
  {
    module: 'people',
    label: 'Persoane / Echipă',
    crud: CRUD(),
    sub: [
      { key: 'canViewCapacity', label: 'Vizualizare capacitate', parent: 'canView' },
      { key: 'canEditAllocations', label: 'Editare alocări', parent: 'canEdit' },
      { key: 'canViewRates', label: 'Vizualizare tarife', parent: 'canView' },
      { key: 'canManageRoles', label: 'Gestionare roluri și conturi', parent: 'canEdit' },
    ],
  },
  {
    module: 'dailySummary',
    label: 'Rezumat zilnic',
    crud: [{ key: 'canView', label: 'Vizualizare' }],
    scope: SUMMARY_SCOPE,
    sub: [
      { key: 'canExportPdf', label: 'Export PDF', parent: 'canView' },
      { key: 'canSendEmail', label: 'Trimite email', parent: 'canView' },
    ],
  },
  {
    module: 'weeklySummary',
    label: 'Rezumat săptămânal',
    crud: [{ key: 'canView', label: 'Vizualizare' }],
    scope: SUMMARY_SCOPE,
    sub: [{ key: 'canExport', label: 'Export', parent: 'canView' }],
  },
  {
    module: 'aiAssistant',
    label: 'Firmio AI',
    crud: [{ key: 'canAccess', label: 'Acces' }],
    sub: [
      { key: 'canCreateTasks', label: 'Creare sarcini', parent: 'canAccess' },
      { key: 'canCreateLeads', label: 'Creare lead-uri', parent: 'canAccess' },
      { key: 'canIngestDocuments', label: 'Procesare documente', parent: 'canAccess' },
      { key: 'canQueryFinancials', label: 'Interogare financiar', parent: 'canAccess' },
      { key: 'canWriteKb', label: 'Scriere bază de cunoștințe', parent: 'canAccess' },
    ],
  },
  {
    module: 'settings',
    label: 'Setări',
    crud: [{ key: 'canAccessSettings', label: 'Acces' }],
    sub: [
      { key: 'canManageCompanies', label: 'Gestionare firme', parent: 'canAccessSettings' },
      { key: 'canEditIntegrations', label: 'Editare integrări', parent: 'canAccessSettings' },
      {
        key: 'canEditExpenseCategories',
        label: 'Editare categorii cheltuieli',
        parent: 'canAccessSettings',
      },
      {
        key: 'canEditLeadPipelines',
        label: 'Editare pipeline-uri lead-uri',
        parent: 'canAccessSettings',
      },
      { key: 'canEditDepartments', label: 'Editare departamente', parent: 'canAccessSettings' },
      { key: 'canEditServices', label: 'Editare servicii', parent: 'canAccessSettings' },
      {
        key: 'canEditTaskStatuses',
        label: 'Editare statusuri sarcini',
        parent: 'canAccessSettings',
      },
      {
        key: 'canEditTaskTemplates',
        label: 'Editare șabloane sarcini',
        parent: 'canAccessSettings',
      },
      {
        key: 'canEditEmailTemplates',
        label: 'Editare template-uri email',
        parent: 'canAccessSettings',
      },
      { key: 'canManageUsers', label: 'Gestionare utilizatori', parent: 'canAccessSettings' },
      { key: 'canManageRoles', label: 'Gestionare roluri', parent: 'canAccessSettings' },
    ],
  },
];

/**
 * AI model-access dropdown options (aiAssistant.modelAccess). The value is a
 * CEILING on the model tier, enforced by the chat (backend/src/lib/ai/
 * modelAccess.ts): the stored keys predate Opus 5.5, so 'sonnet' means
 * "up to Sonnet" and 'both' means every model. Auto stays available to all.
 */
export const MODEL_ACCESS_OPTIONS: { value: string; label: string }[] = [
  { value: 'haiku', label: 'Doar Haiku' },
  { value: 'sonnet', label: 'Haiku și Sonnet' },
  { value: 'both', label: 'Toate (inclusiv Opus)' },
];
